import React from 'react';
import { useNavigate } from 'react-router-dom';

// Icons
const ArrowRight = () => <span>→</span>;
const ExternalLink = () => <span>↗</span>;

// Enhanced Project Card Component
const ProjectCard = ({ title, description, logo, backgroundColor, link, tags, features, isComingSoon, category, year, screenshot }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!isComingSoon) {
      navigate(`/portfolio/${link}`);
    }
  };

  return (
    <div className="enhanced-project-card" onClick={handleClick}>
      <div className="project-card__header">
        <div className="project-visual" style={{ background: backgroundColor }}>
          {screenshot ? (
            <img 
              src={screenshot} 
              alt={`${title} screenshot`} 
              className="project-screenshot"
            />
          ) : (
            <div className="project-logo">{logo}</div>
          )}
          <div className="project-overlay">
            <span className="project-year">{year}</span>
            {isComingSoon && <span className="status-badge status-badge--coming">Coming Soon</span>}
          </div>
        </div>
      </div>

      <div className="project-card__body">
        <div className="project-meta">
          <span className="project-category">{category}</span>
          <div className="project-tags">
            {tags && tags.slice(0, 2).map((tag, index) => (
              <span key={index} className="project-tag">{tag}</span>
            ))}
          </div>
        </div>

        <div className="project-content">
          <h3 className="project-title">{title}</h3>
          <p className="project-description">{description}</p>
        </div>

        <div className="project-highlights">
          {features && features.slice(0, 3).map((feature, index) => (
            <div key={index} className="highlight-item">
              <span className="highlight-dot"></span>
              <span className="highlight-text">{feature}</span>
            </div>
          ))}
        </div>

        <div className="project-action">
          {isComingSoon ? (
            <span className="action-disabled">
              Coming Soon
            </span>
          ) : (
            <div className="action-link">
              <span>View Project</span>
              <ArrowRight />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PortfolioSection = ({ showHeader = true, isHomePage = false }) => {
  const navigate = useNavigate();
  
  const projects = [
    {
      title: "BSides Albuquerque",
      link: "bsides-albuquerque",
      description: "Modern conference platform with interactive features, speaker management, and seamless registration experience.",
      backgroundColor: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      logo: "🎯",
      screenshot: "/img/portfolio/bsides/bsides-header.png", 
      category: "Conference Platform",
      year: "2025",
      tags: ["React", "Event Management", "Registration"],
      features: ["Event Redirecting", "Speaker Profiles", "Live Updates", "Mobile Responsive"]
    },
    {
      title: "RBSEC",
      link: "rbsec",
      description: "Advanced cybersecurity platform with AI-powered threat detection and comprehensive security assessment tools.",
      backgroundColor: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      logo: "🔒",
      screenshot: "/img/portfolio/rbsec/rbsec.png", 
      category: "Cybersecurity Platform",
      year: "2024",
      tags: ["React", "Node.js", "AI/ML", "Security"],
      features: ["Real-time Threat Detection", "Security Assessments", "Training Modules", "3D Visualizations"]
    },
    {
      title: "Nupost",
      link: "nupost",
      description: "Creative mobile app for designing stunning posters with AI-powered templates and collaborative features.",
      backgroundColor: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      logo: "🎨",
      screenshot: "/img/portfolio/nupost/nupost-header.png", 
      category: "Mobile Application",
      year: "2024",
      tags: ["React Native", "AI Design", "Creative Tools"],
      features: ["AI Templates", "Collaborative Design", "Social Sharing", "Export Options"]
    },
    {
      title: "Pragunmart",
      link: "pragunmart", 
      description: "Full-featured e-commerce platform with intelligent inventory management and seamless payment integration.",
      backgroundColor: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      logo: "🛒",
      screenshot: "/img/portfolio/pragunmart/header.png", 
      category: "E-commerce Platform",
      year: "2022",
      tags: ["MERN Stack", "E-commerce", "Analytics"],
      features: ["Smart Inventory", "Payment Gateway", "Analytics Dashboard", "Mobile App"]
    }
  ];

  const displayProjects = isHomePage ? projects.slice(0, 3) : projects;

  return (
    <section className="portfolio-section">
      <div className="container">
        {showHeader && (
          <div className="section-header">
            <div className="section__badge">
              <span>🚀</span>
              <span>Featured Work</span>
            </div>
            
            <h2 className="section__title">
              Recent <span className="text--accent">Projects</span>
            </h2>
            
            <p className="section__description">
              Explore my latest work where innovation meets functionality. Each project represents a unique challenge solved with modern technology and creative thinking.
            </p>
          </div>
        )}

        <div className="enhanced-portfolio-grid">
          {displayProjects.map((project, index) => (
            <ProjectCard key={index} {...project} />
          ))}
        </div>

        {isHomePage && (
          <div className="portfolio-cta">
            <button 
              className="cta-primary-btn"
              onClick={() => navigate('/case-studies')}
            >
              <span>View All Projects</span>
              <ArrowRight />
            </button>
            <p className="cta-note">
              💡 Discover detailed case studies and project insights
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PortfolioSection;